import React from 'react';
import { Fuel, Globe, Instagram, Facebook, Smartphone, BookOpenText, Wrench } from 'lucide-react';
import { motion } from 'framer-motion';
import Button from '../ui/Button';
import SEO from '../seo/SEO';
import { fadeInUp, stagger } from '../../lib/animations';

// UTMs padrão para esta página de links + referenciador do site (ref)
const UTM_BASE = 'utm_source=link_page&utm_medium=social&utm_campaign=gas2_linkhub';

// Adiciona UTMs e o referenciador do site (ref=gas2.app)
const withUtm = (url: string, content: string) => {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${UTM_BASE}&utm_content=${content}&ref=gas2.app`;
};

const links = [
  {
    label: 'Instagram',
    href: withUtm('https://instagram.com/gas2.app', 'instagram'),
    icon: Instagram,
    variant: 'primary' as const,
  },
  {
    label: 'Facebook',
    href: withUtm('https://facebook.com/gas2app', 'facebook'),
    icon: Facebook,
    variant: 'outline' as const,
  },
  {
    label: 'Site oficial',
    href: withUtm('https://gas2.app', 'site'),
    icon: Globe,
    variant: 'outline' as const,
  },
  {
    label: 'Blog',
    href: withUtm('https://gas2.app/blog', 'blog'),
    icon: BookOpenText,
    variant: 'outline' as const,
  },
  {
    label: 'Ferramentas',
    href: withUtm('https://gas2.app/ferramentas', 'ferramentas'),
    icon: Wrench,
    variant: 'outline' as const,
  },
  {
    label: 'Baixar no Android',
    href: withUtm(
      'https://play.google.com/store/apps/details?id=info.marcussoftware.gas2&hl=pt-BR',
      'android'
    ),
    icon: Smartphone,
    variant: 'dark' as const,
  },
  {
    label: 'Baixar no iOS',
    href: withUtm(
      'https://apps.apple.com/br/app/gas2-controle-de-combust%C3%ADvel/id6755345109',
      'ios'
    ),
    icon: Smartphone,
    variant: 'dark' as const,
  },
  // Canal do Radar Gas2 no WhatsApp — inclui UTMs e referenciador
  {
    label: 'Radar Gas2 (WhatsApp)',
    href: withUtm('https://whatsapp.com/channel/0029VbCYZ2TFi8xhMTsye51K', 'whatsapp_channel'),
    icon: Smartphone,
    variant: 'primary' as const,
  },
];

const LinkPage: React.FC = () => {
  return (
    <>
      <SEO
        title="Links Oficiais do Gas2"
        description="Acesse os links oficiais do Gas2: Instagram, Facebook, blog e downloads para Android e iOS."
        canonicalPath="/link"
      />

      <main className="min-h-screen bg-slate-950 px-4 py-10 text-slate-100 sm:px-6">
        <motion.section
          className="mx-auto flex w-full max-w-xl flex-col items-center rounded-3xl border border-slate-800 bg-slate-900/80 p-6 text-center shadow-2xl backdrop-blur sm:p-8"
          variants={stagger}
          initial="hidden"
          animate="visible"
        >
          <motion.div
            variants={fadeInUp}
            className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-400"
          >
            <Fuel className="h-8 w-8 text-slate-900" strokeWidth={2} />
          </motion.div>

          <motion.h1 variants={fadeInUp} className="font-display text-3xl font-bold text-white sm:text-4xl">
            Gas2
          </motion.h1>

          <motion.p variants={fadeInUp} className="mt-3 max-w-md text-sm leading-relaxed text-slate-300 sm:text-base">
            O Gas2 ajuda voce a controlar abastecimentos, custos por km e manutencao do veiculo em um
            unico app.
          </motion.p>

          <motion.div
            variants={fadeInUp}
            className="mt-8 grid w-full grid-cols-1 gap-3"
            aria-label="Links do Gas2"
          >
            {links.map((link) => (
              <Button
                key={link.label}
                href={link.href}
                icon={link.icon}
                target="_blank"
                size="lg"
                variant={link.variant}
                className="w-full"
              >
                {link.label}
              </Button>
            ))}
          </motion.div>
        </motion.section>
      </main>
    </>
  );
};

export default LinkPage;


